/**
 * Hero section — premium reveal with animated SVG wordmark.
 *
 * Sequence:
 *   1. Tag pill fades in
 *   2. Logo scales/rotates in with back-out ease
 *   3. SVG wordmark plays its per-path convergence animation
 *   4. Tagline letter-by-letter reveal
 *   5. CTA buttons stagger up
 *   6. Scroll hint appears
 */
import { gsap } from 'gsap'
import type { Env } from '../env'
import { createIceButton } from '../ui/iceButton'
import { splitLetters } from '../ui/splitText'
import { mountWordmark } from '../ui/wordmark'

export async function mountHero(env: Env): Promise<void> {
  const tag = document.querySelector('.hero__tag') as HTMLElement | null
  const logo = document.getElementById('hero-logo') as HTMLImageElement | null
  const wordmarkSlot = document.getElementById('hero-wordmark') as HTMLDivElement | null
  const tagline = document.getElementById('hero-tagline') as HTMLParagraphElement | null
  const ctas = document.getElementById('hero-ctas') as HTMLDivElement | null
  const scrollHint = document.querySelector('.hero__scroll-hint') as HTMLElement | null

  if (!tag || !logo || !wordmarkSlot || !tagline || !ctas || !scrollHint) return

  // Split tagline into letters but keep each word together (word-level wrap)
  const taglineChars = splitLetters(tagline, tagline.textContent ?? '')

  // Build CTAs
  ctas.innerHTML = ''
  const primaryBtn = createIceButton({
    label: 'Pedir agora',
    size: 'large',
    onClick: () => {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
  })
  const secondaryBtn = createIceButton({
    label: 'Ver produtos',
    variant: 'ghost',
    size: 'large',
    onClick: () => {
      document.getElementById('products')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
  })
  ctas.appendChild(primaryBtn)
  ctas.appendChild(secondaryBtn)

  // Initial states (hide everything before reveal)
  gsap.set(tag, { opacity: 0, y: -20 })
  gsap.set(logo, { opacity: 0, scale: 0.6, rotate: -8 })
  gsap.set(wordmarkSlot, { opacity: 0, y: 20 })
  gsap.set(taglineChars, { opacity: 0, y: 24 })
  gsap.set(ctas.children, { opacity: 0, y: 24 })
  gsap.set(scrollHint, { opacity: 0, y: 12 })

  // Mount wordmark in parallel with font loading
  const [wordmark] = await Promise.all([
    mountWordmark(wordmarkSlot, { reducedMotion: env.reducedMotion }),
    document.fonts.ready.catch(() => {}),
  ])

  if (env.reducedMotion) {
    gsap.set(
      [tag, logo, wordmarkSlot, ...taglineChars, ...Array.from(ctas.children), scrollHint],
      { opacity: 1, y: 0, scale: 1, rotate: 0 },
    )
    wordmark?.play()
    return
  }

  const tl = gsap.timeline({ defaults: { ease: 'power3.out' } })

  tl.to(tag, { opacity: 1, y: 0, duration: 0.5 })

  tl.to(
    logo,
    {
      opacity: 1,
      scale: 1,
      rotate: 0,
      duration: 1,
      ease: 'back.out(1.6)',
    },
    '-=0.2',
  )

  // Reveal the wordmark slot then trigger the per-path animation
  tl.to(
    wordmarkSlot,
    {
      opacity: 1,
      y: 0,
      duration: 0.4,
      onStart: () => wordmark?.play(),
    },
    '-=0.5',
  )

  tl.to(
    taglineChars,
    {
      opacity: 1,
      y: 0,
      duration: 0.4,
      stagger: 0.018,
      ease: 'power2.out',
    },
    '-=0.4',
  )

  tl.to(
    ctas.children,
    {
      opacity: 1,
      y: 0,
      duration: 0.6,
      stagger: 0.12,
    },
    '-=0.15',
  )

  tl.to(scrollHint, { opacity: 1, y: 0, duration: 0.5 }, '-=0.1')
}
